import { Platform } from 'react-native';

/** Saves a text file: a download on the web; on phones the share sheet (save to Files, send…). */
export async function saveTextFile(content: string, fileName: string, mime: string) {
  if (Platform.OS === 'web') {
    const url = URL.createObjectURL(new Blob([content], { type: `${mime};charset=utf-8` }));
    const link = document.createElement('a');
    link.href = url; link.download = fileName; link.style.display = 'none';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return;
  }
  const { Share } = await import('react-native');
  try {
    const FileSystem = await import('expo-file-system/legacy');
    const uri = `${FileSystem.cacheDirectory}${fileName}`;
    await FileSystem.writeAsStringAsync(uri, content, { encoding: FileSystem.EncodingType.UTF8 });
    // Android's share sheet takes text, not file URLs.
    await Share.share(Platform.OS === 'ios' ? { url: uri, title: fileName } : { message: content, title: fileName });
  } catch {
    await Share.share({ message: content, title: fileName });
  }
}

// A4 in millimetres, with the page margin the document is laid out for.
const PAGE_W = 210, PAGE_H = 297, MARGIN = 12;
// Elements a page break may fall before (never through a line of text, a table row or a chart).
const BREAKABLE = 'header, h1, h2, h3, p, li, tr, .stats, .chart, .question, section, .answer';

/**
 * Saves a printable HTML document as a PDF file: on the web it is laid out off screen, drawn
 * (html2canvas — Arabic shaping and right-to-left come from the browser itself) and cut into A4
 * pages between lines, rows and charts, then downloaded; on iPhone a PDF file through the share
 * sheet; on Android the system print dialog (which also saves as PDF).
 */
export async function savePdf(html: string, fileName: string) {
  if (Platform.OS === 'web') {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
    const pxPerMm = 96 / 25.4, contentW = Math.round((PAGE_W - MARGIN * 2) * pxPerMm);
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    // Off screen but laid out at the page's content width (display:none would not lay it out).
    Object.assign(frame.style, { position: 'fixed', left: '-10000px', top: '0', width: `${contentW}px`, height: '1000px', border: '0' });
    document.body.appendChild(frame);
    try {
      const doc = frame.contentWindow!.document;
      doc.open(); doc.write(html); doc.close();
      Object.assign(doc.body.style, { margin: '0', width: `${contentW}px`, background: '#ffffff' });
      await (doc as any).fonts?.ready;
      frame.style.height = `${doc.body.scrollHeight + 20}px`;
      const scale = 2;
      const canvas = await html2canvas(doc.body, { scale, backgroundColor: '#ffffff', windowWidth: contentW, width: contentW, logging: false });
      // Where a page may end: the top of each block, in canvas pixels.
      const bodyTop = doc.body.getBoundingClientRect().top;
      const breaks = [...new Set(Array.from(doc.body.querySelectorAll(BREAKABLE)).map(el => Math.round((el.getBoundingClientRect().top - bodyTop) * scale)))].sort((a, b) => a - b);
      const pageHeightPx = Math.floor((PAGE_H - MARGIN * 2) * pxPerMm * scale);
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
      const mmPerPx = (PAGE_W - MARGIN * 2) / canvas.width;
      let top = 0, page = 0;
      while (top < canvas.height - 2) {
        let end = Math.min(top + pageHeightPx, canvas.height);
        if (end < canvas.height) {
          // The last block start that fits, unless that would leave the page mostly empty.
          const cut = breaks.filter(b => b > top + pageHeightPx * 0.4 && b <= end).pop();
          if (cut) end = cut;
        }
        const slice = document.createElement('canvas');
        slice.width = canvas.width; slice.height = end - top;
        const ctx = slice.getContext('2d')!;
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, top, canvas.width, end - top, 0, 0, canvas.width, end - top);
        if (page++) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN, MARGIN, PAGE_W - MARGIN * 2, (end - top) * mmPerPx);
        pdf.setFontSize(8); pdf.setTextColor(140);
        pdf.text(String(page), PAGE_W / 2, PAGE_H - 5, { align: 'center' });
        top = end;
      }
      pdf.save(fileName);
    } finally {
      frame.remove();
    }
    return;
  }
  const Print = await import('expo-print');
  if (Platform.OS === 'android') { await Print.printAsync({ html }); return; }
  const { uri } = await Print.printToFileAsync({ html });
  const { Share } = await import('react-native');
  await Share.share({ url: uri, title: fileName });
}
